import type {
  AgentConnectionStatus,
  ChatMessage,
  ChatSession,
  CustomFolder,
  ModelInfo,
  SessionFolder,
  UiRender,
} from '@paradigm/terminal-core';

import { createSafeContext } from '../context/createSafeContext';

export interface AgentState {
  readonly status: AgentConnectionStatus;
  readonly errorMessage: string | null;
  /**
   * True when the terminal has everything it needs to show (vs. the login
   * screen): a gateway URL, plus a token for token-only hosts
   * (`supportsPairingRole === false`). Drives the top-level auth gate. Distinct
   * from `status`, which is the live socket state — `isPaired` persists across
   * reconnects and only flips on (re)pair / disconnect.
   */
  readonly isPaired: boolean;
  /** Messages for the active session — hydrated from per-session storage on switch. */
  readonly messages: readonly ChatMessage[];
  readonly agentUrl: string | null;
  /** Ephemeral "agent is doing something" indicator (Thinking / Calling tool / …). */
  readonly processing: {
    readonly label: string;
    readonly detail?: string;
  } | null;
  /** A generative-UI render that has taken over the full chat viewport, if any. */
  readonly fullTerminalUi: {
    readonly messageId: string;
    readonly ui: UiRender;
  } | null;
  /** All known sessions for the current account, persisted across reloads. */
  readonly sessions: readonly ChatSession[];
  /** User-created folders for grouping sessions. */
  readonly customFolders: readonly CustomFolder[];
  /** The session currently shown in the Chat tab. */
  readonly activeSessionId: string | null;
  /** Run ids the user explicitly cancelled via Stop (post-stop frames are dropped). */
  readonly cancelledRunIds: ReadonlySet<string>;
  /** Bedrock models offered by the picker (from the gateway's `/api/models`). */
  readonly models: readonly ModelInfo[];
  /** Bedrock id of the current pick; `null` ⇒ gateway default. */
  readonly selectedModelId: string | null;
}

export interface AgentActions {
  /**
   * Send a chat message. Optional `context` is per-message hidden context
   * appended to the gateway payload (like `systemPreamble`, but per-call) —
   * it does NOT appear in the chat bubble. Used e.g. to attach a `/analyze`d
   * trade's structured data without cluttering the visible transcript.
   */
  readonly sendMessage: (
    content: string,
    opts?: { readonly context?: string },
  ) => void;
  /**
   * Open (or reopen) the gateway connection. An optional `token` is a fresh
   * pairing token (e.g. from the setup form): it's persisted to the token
   * store — replacing any stale device token — before the handshake, so the
   * connect frame carries it as `auth.token`.
   */
  readonly connect: (url?: string, token?: string) => void;
  readonly disconnect: () => void;
  /** Clear messages in the *active* session only. */
  readonly clearMessages: () => void;
  readonly respondToApproval: (approvalId: string, approved: boolean) => void;
  readonly request: <T = unknown>(
    method: string,
    params?: Record<string, unknown>,
  ) => Promise<T>;
  readonly requestPairing: () => Promise<string>;
  readonly sendUiEvent: (params: {
    readonly surfaceId: string;
    readonly componentId?: string;
    readonly event: string;
    readonly data?: Record<string, unknown>;
  }) => void;
  readonly dismissFullTerminalUi: () => void;
  readonly createSession: (opts: {
    readonly folder: SessionFolder;
    readonly isTemporary?: boolean;
  }) => string;
  readonly switchSession: (sessionId: string) => void;
  readonly renameSession: (sessionId: string, title: string) => void;
  readonly pinSession: (sessionId: string, pinned: boolean) => void;
  readonly deleteSession: (sessionId: string) => void;
  readonly setSessionFolder: (
    sessionId: string,
    customFolderId: string | undefined,
  ) => void;
  readonly createCustomFolder: (name: string) => string;
  readonly renameCustomFolder: (folderId: string, name: string) => void;
  readonly pinCustomFolder: (folderId: string, pinned: boolean) => void;
  readonly deleteCustomFolder: (folderId: string) => void;
  readonly stopProcessing: () => void;
  readonly setSelectedModel: (modelId: string) => void;
}

export const INITIAL_AGENT_STATE: AgentState = {
  status: 'disconnected',
  errorMessage: null,
  isPaired: false,
  messages: [],
  agentUrl: null,
  processing: null,
  fullTerminalUi: null,
  sessions: [],
  customFolders: [],
  activeSessionId: null,
  cancelledRunIds: new Set<string>(),
  models: [],
  selectedModelId: null,
};

export const [useAgentState, AgentStateProvider] =
  createSafeContext<AgentState>('AgentState');

export const [useAgentActions, AgentActionsProvider] =
  createSafeContext<AgentActions>('AgentActions');

/**
 * Narrow, host-facing agent API — the ONLY agent surface re-exported from the
 * package's public entry (`useTerminalAgent`). Host `agentEffects` (see
 * `TerminalProps.agentEffects`) use it to drive the agent from host state:
 * e.g. a block-trade context pump calling `request(...)`, or a pending-message
 * drain calling `sendMessage(...)` once `status === 'authenticated'`.
 *
 * The full `AgentActions` (28 mutating methods) and `AgentState` stay internal
 * so they don't become a published semver contract.
 */
export interface TerminalAgentApi {
  /** Current connection/auth status — gate effects on `'authenticated'`. */
  readonly status: AgentConnectionStatus;
  /**
   * Send a chat message as the user. The host preamble (if any) is applied,
   * and optional `context` is per-message hidden context appended to the
   * gateway payload only — not shown in the chat bubble. Use it to attach
   * structured data (e.g. a `/analyze`d trade) without cluttering the chat.
   */
  readonly sendMessage: (
    content: string,
    opts?: { readonly context?: string },
  ) => void;
  /** Issue a raw gateway RPC (e.g. `set_block_trade_context`). */
  readonly request: <T = unknown>(
    method: string,
    params?: Record<string, unknown>,
  ) => Promise<T>;
}

export function useTerminalAgent(): TerminalAgentApi {
  const { sendMessage, request } = useAgentActions();
  const { status } = useAgentState();
  return { status, sendMessage, request };
}

/**
 * Host-facing AUTH seam — login state plus programmatic sign-in/out, so an
 * enclosing app can drive pairing itself instead of (or alongside) the
 * terminal's built-in login screen. The terminal owns its own credential
 * storage; this just lets the host push credentials in and read the result.
 *
 * Two ways a host signs the terminal in:
 *  - Declarative: pass `gatewayUrl` + `fallbackPairingToken` (or `resolveUrl`)
 *    in `TerminalConfig`; the terminal auto-pairs from them on mount.
 *  - Imperative: render a node in `agentEffects` that fetches credentials
 *    (e.g. after the host's own OAuth) and calls `login(url, token)`.
 *
 * `isLoggedIn` mirrors the internal pairing state; `logout()` unpairs (drops
 * the stored token) and returns to the login screen.
 */
export interface TerminalAuthApi {
  readonly isLoggedIn: boolean;
  readonly login: (url: string, token?: string) => void;
  readonly logout: () => void;
}

export function useTerminalAuth(): TerminalAuthApi {
  const { connect, disconnect } = useAgentActions();
  const { isPaired } = useAgentState();
  return {
    isLoggedIn: isPaired,
    login: (url: string, token?: string) => connect(url, token),
    logout: () => disconnect(),
  };
}
